import {
  RECORD_QUESTION_TYPES,
  THEMED_QUESTION_TYPES,
  blockIssue,
  reviewTemplateBlocks,
  type HealthTheme,
  type RecordBlock,
  type RecordQuestion,
} from '@hue/contracts';
import {
  Button,
  ChoiceChips,
  IconButton,
  Input,
  RichTextEditor,
  Textarea,
  ToggleChips,
} from '@hue/design-system';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Toggle } from './shared.js';
import { blockKinds, questionTypes } from './helpers.js';

/// Editor de um bloco do modelo. Confere com a mesma regra do servidor antes de aplicar ao
/// rascunho, e aplica a versão limpa: a escolha única que virou texto curto não leva as opções.
export function BlockEditor({
  block,
  isNew,
  themes,
  onApply,
  onCancel,
  onOpenThemes,
}: {
  block: RecordBlock;
  isNew: boolean;
  themes: readonly HealthTheme[];
  onApply: (block: RecordBlock) => void;
  onCancel: () => void;
  onOpenThemes?: (() => void) | undefined;
}) {
  const [draft, setDraft] = useState<RecordBlock>(block);
  const [error, setError] = useState<string | null>(null);
  const known = new Set(themes.map((theme) => theme.key));

  function apply() {
    const issue = blockIssue(draft, known);
    setError(issue);
    if (!issue) onApply(reviewTemplateBlocks([draft], known).blocks[0]!);
  }

  return (
    <div className="grid gap-4 border-y border-brand-500/30 bg-brand-500/5 px-4 py-4 sm:px-5">
      {draft.kind === 'HEADING' || draft.kind === 'SUBHEADING' ? (
        <Input
          label={blockKinds[draft.kind]}
          value={draft.text}
          error={error ?? undefined}
          onChange={(event) => setDraft({ ...draft, text: event.target.value })}
        />
      ) : draft.kind === 'DESCRIPTION' ? (
        <RichTextEditor
          label="Descrição"
          value={draft.html}
          error={error ?? undefined}
          placeholder="Texto de orientação para o cliente"
          onValueChange={(html) => setDraft({ ...draft, html })}
        />
      ) : (
        <QuestionEditor
          question={draft}
          themes={themes}
          error={error}
          onChange={setDraft}
          onOpenThemes={onOpenThemes}
        />
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancelar
        </Button>
        <Button size="sm" onClick={apply}>
          {isNew ? 'Adicionar ao rascunho' : 'Aplicar ao rascunho'}
        </Button>
      </div>
    </div>
  );
}

function QuestionEditor({
  question,
  themes,
  error,
  onChange,
  onOpenThemes,
}: {
  question: RecordQuestion;
  themes: readonly HealthTheme[];
  error: string | null;
  onChange: (question: RecordQuestion) => void;
  onOpenThemes: (() => void) | undefined;
}) {
  const set = (patch: Partial<RecordQuestion>) => onChange({ ...question, ...patch });
  const choice = question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE';
  const themed = THEMED_QUESTION_TYPES.includes(question.type);
  const offered = themes.filter((theme) => theme.active || theme.key === question.themeKey);
  const options = question.options;
  const move = (index: number, delta: number) => {
    const next = [...options];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item!);
    set({ options: next });
  };

  return (
    <div className="grid gap-4">
      {question.type === 'DECLARATION' ? (
        <Textarea
          label="Texto da declaração *"
          value={question.label}
          error={error ?? undefined}
          placeholder="Ex.: Declaro que as informações acima são verdadeiras."
          onChange={(event) => set({ label: event.target.value })}
        />
      ) : (
        <Input
          label="Pergunta *"
          value={question.label}
          error={error ?? undefined}
          placeholder="Ex.: Tem alguma alergia?"
          onChange={(event) => set({ label: event.target.value })}
        />
      )}
      <Input
        label="Texto explicativo (opcional)"
        value={question.help ?? ''}
        placeholder="Aparece embaixo da pergunta, em letra menor."
        onChange={(event) => set({ help: event.target.value || null })}
      />
      <div className="grid gap-2">
        <span className="text-xs font-semibold text-content/70">Tipo de resposta</span>
        <ChoiceChips
          label="Tipo de resposta"
          placeholder="Buscar tipo"
          value={question.type}
          options={RECORD_QUESTION_TYPES.map((type) => ({
            value: type,
            label: questionTypes[type].label,
          }))}
          onChange={(type) => set({ type: type as RecordQuestion['type'] })}
        />
        <p className="text-xs text-content-muted">{questionTypes[question.type].hint}</p>
      </div>

      {choice ? (
        <div className="grid gap-2">
          <span className="text-xs font-semibold text-content/70">Opções *</span>
          {options.map((option, index) => (
            <div key={option.id} className="flex items-end gap-1">
              <div className="min-w-0 flex-1">
                <Input
                  label={`Opção ${String(index + 1)}`}
                  value={option.label}
                  onChange={(event) =>
                    set({
                      options: options.map((item) =>
                        item.id === option.id ? { ...item, label: event.target.value } : item,
                      ),
                    })
                  }
                />
              </div>
              <IconButton
                label={`Subir opção ${String(index + 1)}`}
                icon={<ArrowUp className="size-4" />}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              />
              <IconButton
                label={`Descer opção ${String(index + 1)}`}
                icon={<ArrowDown className="size-4" />}
                disabled={index === options.length - 1}
                onClick={() => move(index, 1)}
              />
              <IconButton
                tone="danger"
                label={`Remover opção ${String(index + 1)}`}
                icon={<Trash2 className="size-4" />}
                onClick={() =>
                  set({
                    options: options.filter((item) => item.id !== option.id),
                    exclusiveOptionId:
                      question.exclusiveOptionId === option.id ? null : question.exclusiveOptionId,
                    flaggedOptionIds: question.flaggedOptionIds.filter((id) => id !== option.id),
                  })
                }
              />
            </div>
          ))}
          <div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => set({ options: [...options, { id: crypto.randomUUID(), label: '' }] })}
            >
              <Plus className="size-4" />
              Opção
            </Button>
          </div>
        </div>
      ) : null}

      {question.type === 'MULTIPLE_CHOICE' && options.length ? (
        <div className="grid gap-2">
          <span className="text-xs font-semibold text-content/70">Opção que anula as outras</span>
          <p className="text-xs text-content-muted">
            Para “Nenhuma”: marcá-la desmarca o resto, e ela nunca entra no Quadro de saúde.
          </p>
          <ChoiceChips
            label="Opção que anula as outras"
            placeholder="Buscar opção"
            value={question.exclusiveOptionId ?? ''}
            options={[
              { value: '', label: 'Nenhuma delas' },
              ...options.map((option, index) => ({
                value: option.id,
                label: option.label || `Opção ${String(index + 1)}`,
              })),
            ]}
            onChange={(id) => set({ exclusiveOptionId: id || null })}
          />
        </div>
      ) : null}

      {question.type === 'YES_NO' ? (
        <div className="grid gap-2">
          <Toggle
            checked={question.detailLabel !== null}
            onChange={(on) => set({ detailLabel: on ? '' : null })}
          >
            Pedir detalhe quando a resposta for Sim
          </Toggle>
          {question.detailLabel !== null ? (
            <Input
              label="O que perguntar no detalhe"
              value={question.detailLabel}
              placeholder="Ex.: Qual?"
              onChange={(event) => set({ detailLabel: event.target.value })}
            />
          ) : null}
        </div>
      ) : null}

      {themed ? (
        <div className="grid gap-2">
          <span className="text-xs font-semibold text-content/70">Tema de saúde</span>
          <p className="text-xs leading-5 text-content-muted">
            Com tema, a resposta positiva vai para o Quadro de saúde e é comparada com as outras
            fichas.
            {onOpenThemes ? (
              <>
                {' '}
                Falta um tema?{' '}
                <button
                  type="button"
                  onClick={onOpenThemes}
                  className="cursor-pointer font-semibold text-brand-400 hover:underline"
                >
                  Crie em Temas de saúde
                </button>
                .
              </>
            ) : null}
          </p>
          <ChoiceChips
            label="Tema de saúde"
            placeholder="Buscar tema"
            value={question.themeKey ?? ''}
            options={[
              { value: '', label: 'Nenhum' },
              ...offered.map((theme) => ({ value: theme.key, label: theme.displayName })),
            ]}
            onChange={(themeKey) => set({ themeKey: themeKey || null })}
          />
          {question.themeKey && question.type === 'SINGLE_CHOICE' ? (
            <div className="grid gap-2">
              <span className="text-xs font-semibold text-content/70">
                Opções que entram no Quadro
              </span>
              <ToggleChips
                label="Opções que entram no Quadro"
                itemNoun="opções"
                selected={question.flaggedOptionIds}
                options={options.map((option, index) => ({
                  value: option.id,
                  label: option.label || `Opção ${String(index + 1)}`,
                }))}
                onChange={(flaggedOptionIds) => set({ flaggedOptionIds })}
              />
            </div>
          ) : question.themeKey ? (
            <p className="text-xs text-content-faint">
              {question.type === 'YES_NO'
                ? 'Entra no Quadro quando a resposta é Sim, com o detalhe.'
                : 'Entra no Quadro qualquer opção marcada, menos a que anula as outras.'}
            </p>
          ) : null}
        </div>
      ) : null}

      <Toggle checked={question.required} onChange={(required) => set({ required })}>
        Obrigatória para salvar a ficha
      </Toggle>
    </div>
  );
}
